import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../utils/axios-instance";
import { useDepartment } from "../stores/departmentStore";
import useAuth from "../stores/authStore";
import { auth } from "../helper/firebaseClient";
import Papa from "papaparse";
import CircularProgress from "@mui/material/CircularProgress";
import toast, { Toaster } from "react-hot-toast";

const SeatUpgrade = ({ orgId: orgIdProp }) => {
  const navigate = useNavigate();
  const { departments, listDepartments } = useDepartment();
  const profileData = useAuth((state) => state.profileData);

  // orgId can come as prop (future use) or from the authenticated user's profile
  const orgId = orgIdProp || profileData?.org_id;

  // ── Subscription state ──────────────────────────────────────────────────────
  const [seatSummary, setSeatSummary] = useState({
    paid: 0,
    used: 0,
    invited: 0,
    available: 0,
  });
  const [planCode, setPlanCode] = useState("");
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [pricePerSeat, setPricePerSeat] = useState(0);

  // ── UI state ─────────────────────────────────────────────────────────────────
  const [additionalSeats, setAdditionalSeats] = useState(1);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [error, setError] = useState(null);

  // ── Invite state ─────────────────────────────────────────────────────────────
  const [inviteMethod, setInviteMethod] = useState("manual");
  const [teamEmails, setTeamEmails] = useState([""]);
  const [teamRoles, setTeamRoles] = useState(["employee"]);
  const [teamDepartments, setTeamDepartments] = useState([""]);
  const [csvFile, setCsvFile] = useState(null);

  // ── Load current subscription ─────────────────────────────────────────────
  useEffect(() => {
    async function load() {
      if (!orgId) return;
      try {
        const res = await api.get("/payments/current/");
        setSeatSummary(
          res.data.seats || { paid: 0, used: 0, invited: 0, available: 0 },
        );
        setPlanCode(res.data.planCode || "");
        setBillingCycle(res.data.billingCycle || "monthly");
        setPricePerSeat(res.data.price || 0);
      } catch {
        toast.error("Failed to load subscription info. Please refresh.");
      } finally {
        setPageLoading(false);
      }
    }
    load();
    listDepartments();
  }, [orgId]);

  // ── Derived values ────────────────────────────────────────────────────────
  // All invitable slots = pre-existing unfilled seats + seats being purchased
  const totalInvitableSeats = seatSummary.available + additionalSeats;

  // Only count rows where an email has actually been typed
  const validEmailRows = teamEmails
    .map((email, i) => ({
      email,
      dept: teamDepartments[i],
      role: teamRoles[i],
    }))
    .filter((r) => r.email.trim() && r.email.includes("@"));

  const seatsRemaining = totalInvitableSeats - validEmailRows.length;

  // Cost = only the NEW seats (seatSummary.available are already paid)
  const totalCost = additionalSeats * pricePerSeat;

  // ── Seat input ────────────────────────────────────────────────────────────
  const handleSeatChange = (e) => {
    const val = parseInt(e.target.value || 1);
    if (val < 1) return;
    setAdditionalSeats(val);
    setError(null);
  };

  // ── Invite row handlers ───────────────────────────────────────────────────
  const handleAddEmailRow = () => {
    if (validEmailRows.length >= totalInvitableSeats) {
      toast.error(
        `All ${totalInvitableSeats} seat(s) are filled. Increase the seat count to invite more.`,
      );
      return;
    }
    setTeamEmails([...teamEmails, ""]);
    setTeamRoles([...teamRoles, "employee"]);
    setTeamDepartments([...teamDepartments, ""]);
    setError(null);
  };

  const handleRemoveEmailRow = (index) => {
    setTeamEmails(teamEmails.filter((_, i) => i !== index));
    setTeamRoles(teamRoles.filter((_, i) => i !== index));
    setTeamDepartments(teamDepartments.filter((_, i) => i !== index));
  };

  const handleEmailChange = (index, value) => {
    const updated = [...teamEmails];
    updated[index] = value;
    setTeamEmails(updated);
  };

  const handleRoleChange = (index, value) => {
    const updated = [...teamRoles];
    updated[index] = value;
    setTeamRoles(updated);
  };

  const handleDeptChange = (index, value) => {
    const updated = [...teamDepartments];
    updated[index] = value;
    setTeamDepartments(updated);
  };

  // ── CSV upload ────────────────────────────────────────────────────────────
  const handleCsvUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setCsvFile(file);

    Papa.parse(file, {
      header: true,
      complete: (results) => {
        const emails = [];
        const roles = [];
        results.data.forEach((row) => {
          const email = row.email || row.Email || row.EMAIL;
          const role = row.role || row.Role || row.ROLE || "employee";
          if (email && email.trim() && email.includes("@")) {
            emails.push(email.trim());
            roles.push(role.trim() || "employee");
          }
        });

        if (emails.length === 0) {
          toast.error("No valid emails found in CSV.");
          return;
        }

        if (emails.length > totalInvitableSeats) {
          toast.error(
            `CSV has ${emails.length} emails but only ${totalInvitableSeats} seat(s) available. Extra rows will be trimmed.`,
          );
        }

        const sliced = emails.slice(0, totalInvitableSeats);
        setTeamEmails(sliced);
        setTeamRoles(roles.slice(0, totalInvitableSeats));
        setTeamDepartments(new Array(sliced.length).fill(""));
        toast.success(`${sliced.length} email(s) loaded from CSV.`);
      },
      error: () => toast.error("Failed to parse CSV file."),
    });
  };

  // ── Validation ────────────────────────────────────────────────────────────
  const validate = () => {
    if (additionalSeats < 1) return "Must purchase at least 1 seat.";

    if (validEmailRows.length > totalInvitableSeats) {
      return `Too many invites. You only have ${totalInvitableSeats} seat(s) available.`;
    }

    for (const { email, dept, role } of validEmailRows) {
      if (!dept) return `Please select a department for ${email}.`;
      if (!role) return `Please select a role for ${email}.`;
      // Guard: org_admin cannot be assigned via invites
      if (role === "org_admin")
        return `Cannot assign org_admin role to invited members.`;
    }

    return null;
  };

  // ── Continue → Checkout ───────────────────────────────────────────────────
  const handleContinue = async () => {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Step 1: Store pending invites
      // process_pending_invites_for_org fires AFTER payment succeeds in verify_payment
      if (validEmailRows.length > 0) {

        const idToken = await auth.currentUser.getIdToken(true);

        await api.post("/invite", {
          orgId,
          idToken,
          invites: validEmailRows.map(({ email, dept, role }) => ({
            email,
            dept,
            role,
          })),
        });
      }

      // Step 2: Go to checkout with isRenewal: true
      navigate("/checkout", {
        state: {
          orgId, // CheckoutPage reads state.orgId
          planCode,
          billingCycle,
          seats: additionalSeats, // ONLY new seats — already-paid available seats are free
          isRenewal: true,
        },
      });
    } catch (e) {
      const msg =
        e?.response?.data?.error || "Something went wrong. Please try again.";
      toast.error(msg);
      setLoading(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  if (pageLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <CircularProgress style={{ color: "#5E5ADB" }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-start justify-center py-12 px-4">
      <Toaster position="top-right" />

      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-sm border border-gray-100">
        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div className="px-8 pt-8 pb-6">
          <h2 className="text-2xl font-bold text-gray-900">Upgrade Seats</h2>
          <p className="text-gray-500 mt-1">
            Purchase additional seats and invite your team
          </p>
        </div>

        <div className="px-8 pb-8 space-y-8">
          {/* ── Current seat summary ────────────────────────────────────── */}
          {seatSummary.paid > 0 && (
            <div className="flex flex-wrap gap-3 text-sm">
              {[
                {
                  label: "paid",
                  value: seatSummary.paid,
                  cls: "bg-gray-100 text-gray-600",
                },
                {
                  label: "used",
                  value: seatSummary.used,
                  cls: "bg-gray-100 text-gray-600",
                },
                {
                  label: "invited",
                  value: seatSummary.invited,
                  cls: "bg-amber-50 border border-amber-100 text-amber-700",
                },
                {
                  label: "available",
                  value: seatSummary.available,
                  cls: "bg-green-50 border border-green-100 text-green-700",
                },
              ].map(({ label, value, cls }) => (
                <span key={label} className={`px-3 py-1.5 rounded-lg ${cls}`}>
                  <span className="font-semibold">{value}</span> {label}
                </span>
              ))}
            </div>
          )}

          {/* ── Seat count input ────────────────────────────────────────── */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Additional quantity of seats required
            </label>
            <input
              type="number"
              min={1}
              value={additionalSeats}
              onChange={handleSeatChange}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-gray-900 text-base focus:outline-none focus:ring-2 focus:ring-[#5E5ADB] focus:border-transparent transition"
            />
            <div className="flex flex-wrap items-start justify-between mt-2 gap-1">
              {/* <p className="text-sm text-gray-400">
                Set how many new seats you want to purchase
              </p> */}
              {pricePerSeat > 0 && (
                <p className="text-sm font-medium text-red-500">
                  {additionalSeats} seat(s) × ₹{pricePerSeat} / user = ₹
                  {totalCost.toLocaleString("en-IN")}{" "}
                  <span className="text-gray-400 font-normal">
                    (to be paid at checkout)
                  </span>
                </p>
              )}
            </div>
          </div>

          {/* ── Invite method toggle ────────────────────────────────────── */}
          <div className="flex gap-3">
            {["manual", "csv"].map((method) => (
              <button
                key={method}
                onClick={() => setInviteMethod(method)}
                className={`flex-1 py-3 rounded-xl font-medium text-sm transition-all border ${
                  inviteMethod === method
                    ? "bg-[#5E5ADB] text-white border-[#5E5ADB]"
                    : "bg-white text-gray-600 border-gray-200 hover:border-[#5E5ADB] hover:text-[#5E5ADB]"
                }`}
              >
                {method === "manual" ? "Add Emails Manually" : "Upload CSV"}
              </button>
            ))}
          </div>

          {/* ── Manual invite rows ──────────────────────────────────────── */}
          {inviteMethod === "manual" && (
            <div className="space-y-3">
              {teamEmails.map((email, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <input
                    type="email"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => handleEmailChange(i, e.target.value)}
                    className="flex-1 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#5E5ADB] focus:border-transparent transition"
                  />

                  <select
                    value={teamDepartments[i] || ""}
                    onChange={(e) => handleDeptChange(i, e.target.value)}
                    className="w-44 border border-gray-200 rounded-xl px-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#5E5ADB] focus:border-transparent transition bg-white text-gray-700"
                  >
                    <option value="">Select Dept</option>
                    {departments.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.emoji ? `${d.emoji} ` : ""}
                        {d.name}
                      </option>
                    ))}
                  </select>

                  <select
                    value={teamRoles[i] || "employee"}
                    onChange={(e) => handleRoleChange(i, e.target.value)}
                    className="w-36 border border-gray-200 rounded-xl px-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#5E5ADB] focus:border-transparent transition bg-white text-gray-700"
                  >
                    <option value="employee">Employee</option>
                    <option value="super_admin">Super Admin</option>
                  </select>

                  {teamEmails.length > 1 && (
                    <button
                      onClick={() => handleRemoveEmailRow(i)}
                      className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-xl border border-gray-200 text-gray-400 hover:text-red-500 hover:border-red-200 transition-all"
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}

              <button
                onClick={handleAddEmailRow}
                className="w-full py-3 border-2 border-dashed border-gray-200 rounded-xl text-sm text-gray-500 hover:border-[#5E5ADB] hover:text-[#5E5ADB] transition-all font-medium"
              >
                + Add Another Email
              </button>

              {/* Seats remaining counter */}
              <p className="text-sm text-gray-500">
                {seatsRemaining > 0 ? (
                  <>
                    {seatsRemaining} seat(s) remaining out of{" "}
                    {totalInvitableSeats}.
                    {seatSummary.available > 0 && (
                      <span className="text-gray-400 ml-1">
                        (includes {seatSummary.available} previously unused seat
                        {seatSummary.available !== 1 ? "s" : ""})
                      </span>
                    )}
                  </>
                ) : (
                  <span className="text-amber-600 font-medium">
                    All {totalInvitableSeats} seat(s) filled. Increase the seat
                    count above to invite more.
                  </span>
                )}
              </p>
            </div>
          )}

          {/* ── CSV upload ──────────────────────────────────────────────── */}
          {inviteMethod === "csv" && (
            <div className="space-y-4">
              <div className="border-2 border-dashed border-gray-200 rounded-xl p-10 text-center hover:border-[#5E5ADB] transition-colors">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleCsvUpload}
                  className="hidden"
                  id="csv-upload"
                />
                <label
                  htmlFor="csv-upload"
                  className="cursor-pointer flex flex-col items-center"
                >
                  <svg
                    className="w-12 h-12 text-gray-300 mb-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                  <p className="text-gray-600 font-medium mb-1">
                    Click to upload CSV file
                  </p>
                  <p className="text-sm text-gray-400">
                    CSV should have an{" "}
                    <span className="font-mono bg-gray-100 px-1 rounded">
                      email
                    </span>{" "}
                    column and optionally a{" "}
                    <span className="font-mono bg-gray-100 px-1 rounded">
                      role
                    </span>{" "}
                    column
                  </p>
                </label>
              </div>

              {csvFile && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                  <p className="text-green-800 font-medium text-sm">
                    ✓ {csvFile.name} uploaded successfully
                  </p>
                  <p className="text-sm text-green-600 mt-0.5">
                    {teamEmails.filter((e) => e.trim()).length} email(s) loaded
                  </p>
                </div>
              )}

              {/* After CSV load, still show rows for dept assignment */}
              {csvFile && teamEmails.filter((e) => e.trim()).length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-700">
                    Assign departments to imported emails:
                  </p>
                  {teamEmails.map((email, i) =>
                    email.trim() ? (
                      <div key={i} className="flex gap-2 items-center">
                        <span className="flex-1 border border-gray-100 bg-gray-50 rounded-xl px-4 py-3 text-sm text-gray-600 truncate">
                          {email}
                        </span>
                        <select
                          value={teamDepartments[i] || ""}
                          onChange={(e) => handleDeptChange(i, e.target.value)}
                          className="w-44 border border-gray-200 rounded-xl px-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#5E5ADB] transition bg-white text-gray-700"
                        >
                          <option value="">Select Dept</option>
                          {departments.map((d) => (
                            <option key={d.id} value={d.id}>
                              {d.emoji ? `${d.emoji} ` : ""}
                              {d.name}
                            </option>
                          ))}
                        </select>
                        <select
                          value={teamRoles[i] || "employee"}
                          onChange={(e) => handleRoleChange(i, e.target.value)}
                          className="w-36 border border-gray-200 rounded-xl px-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#5E5ADB] transition bg-white text-gray-700"
                        >
                          <option value="employee">Employee</option>
                          <option value="super_admin">Super Admin</option>
                        </select>
                      </div>
                    ) : null,
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Tip ────────────────────────────────────────────────────── */}
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
            <p className="text-sm text-blue-800">
              💡 You can skip inviting now and add team members later from your
              dashboard. Invitations are sent after successful payment.
            </p>
          </div>

          {/* ── Error ──────────────────────────────────────────────────── */}
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {/* ── Action ─────────────────────────────────────────────────── */}
          <div className="flex justify-end pt-2">
            <button
              onClick={handleContinue}
              disabled={loading}
              className="px-8 py-3 bg-[#5E5ADB] text-white rounded-xl font-medium hover:bg-[#4C47C7] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm"
            >
              {loading ? (
                <>
                  <CircularProgress size={16} style={{ color: "white" }} />
                  <span>Processing...</span>
                </>
              ) : (
                "Continue to Payment"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SeatUpgrade;